// src/pages/TrackCreation/Step1.tsx
import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTrackCreation } from "../../../../../context/TrackCreationContext";
import { useTranslation } from "react-i18next";
import { QuizQuestionProps, RouteParamsWithLanguage } from "../../../../../types/screensTypes";
import Button from "../../../../../components/ui/Button";
import { saveQuiz, getQuizById } from "../../../../../api";
import RadioButton from "../../../../../components/common/RadioButton";
import TextInput from "../../../../../components/ui/TextInput";
import { idToLanguage } from "../../../../../utils/idtolanguage";
import { useGlobalState } from "../../../../../context/useGlobalState";
import styles from "./QuizContent.module.css";
import stepStyles from "../../createStep.module.css";

interface Answer {
    id: string;
    text: string;
    isGoodAnswer: boolean;
    position?: number;
}

const MAX_ANSWERS = 4;

const QuizQuestion: React.FC<QuizQuestionProps> = ({ navigateToNext }) => {
    const { trackData } = useTrackCreation();
    const { quizId, language } = useParams<RouteParamsWithLanguage>();
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data } = useGlobalState();
    const [question, setQuestion] = useState<string>("");
    const [explanation, setExplanation] = useState<string>("");
    const [answers, setAnswers] = useState<Answer[]>([]);

    const handleAddAnswer = (): void => {
        if (answers.length >= MAX_ANSWERS) return;
        const newAnswer: Answer = {
            id: Date.now().toString(),
            text: "",
            isGoodAnswer: false,
            position: answers.length + 1,
        };
        setAnswers((prevAnswers) => [...prevAnswers, newAnswer]);
    };

    const handleRemoveAnswer = (id: string): void => {
        setAnswers((prev) => prev.filter((answer) => answer.id !== id));
    };

    const pageTitle = `${t("questionAnswer")}${idToLanguage(language || "", data?.languagesAI, t)}`;
    const hasCorrectAnswer = answers.some((answer) => answer.isGoodAnswer);

    const handleSaveQuiz = async (): Promise<void> => {
        if (!hasCorrectAnswer) return;
        const token = localStorage.getItem("token");
        const trackId = trackData.trackId;
        if (!token || !trackId || !quizId || !language) {
            return;
        }
        const response = await saveQuiz({
            token,
            trackId,
            quizId,
            language,
            question,
            explanation,
            choices: answers,
        });

        if (response && response.message) {
            navigateToNext(quizId, language);
        }
    };

    const getQuizInfo = async (trackId: string, quizId: string, lang: string): Promise<void> => {
        const token = localStorage.getItem("token");
        if (!token) {
            return;
        }
        const quizData = await getQuizById({
            token,
            trackId,
            quizId,
            language: lang,
        });

        const localizedInfo = quizData?.trackQuizLocalized?.[0] || null;
        if (localizedInfo) {
            setQuestion(localizedInfo.question);
            setExplanation(localizedInfo.explanation);
            setAnswers(localizedInfo.trackQuizQuestion);
        }
    };

    useEffect(() => {
        if (quizId && quizId !== "" && trackData.trackId && language) {
            getQuizInfo(trackData.trackId, quizId, language);
        }
    }, [quizId, language, trackData.trackId]);

    const handleAnswerTextChange = (id: string, newText: string): void => {
        setAnswers((prev) => prev.map((answer) => (answer.id === id ? { ...answer, text: newText } : answer)));
    };

    const handleCorrectAnswerChange = (id: string): void => {
        setAnswers((prev) =>
            prev.map((answer) => ({
                ...answer,
                isGoodAnswer: answer.id === id,
            })),
        );
    };

    return (
        <div className={styles.root}>
            <h1 className={styles.title}>{pageTitle}</h1>

            <section className={styles.section}>
                <h2 className={styles.heading}>{t("question")}</h2>
                <div className={styles.questionInput}>
                    <TextInput
                        placeholder={`${t("question")} 1`}
                        value={question}
                        size="medium"
                        onChange={(e) => setQuestion(e.target.value)}
                    />
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.heading}>{t("answers")}</h2>
                <div className={styles.answers}>
                    {answers.map((answer, index) => (
                        <div key={answer.id} className={styles.answerRow}>
                            <div className={styles.answerInput}>
                                <TextInput
                                    placeholder={`${t("answer")} ${index + 1}`}
                                    value={answer.text}
                                    size="medium"
                                    onChange={(e) => handleAnswerTextChange(answer.id, e.target.value)}
                                />
                            </div>
                            <div className={styles.answerCheck}>
                                <RadioButton
                                    checked={answer.isGoodAnswer}
                                    onChange={() => handleCorrectAnswerChange(answer.id)}
                                    center={true}
                                    label=""
                                />
                            </div>
                            <Button
                                variant="danger"
                                shape="pill"
                                className={styles.deleteBtn}
                                label={t("removeAnswer")}
                                onClick={() => handleRemoveAnswer(answer.id)}
                            />
                        </div>
                    ))}
                </div>
                <Button
                    variant="primary"
                    shape="pill"
                    className={styles.addBtn}
                    label={t("addAnswer")}
                    onClick={handleAddAnswer}
                    disabled={answers.length >= MAX_ANSWERS}
                />
            </section>

            <section className={styles.section}>
                <h2 className={styles.heading}>{t("explanation")}</h2>
                <div className={styles.explanationInput}>
                    <TextInput
                        placeholder={t("explanation")}
                        value={explanation}
                        size="medium"
                        onChange={(e) => setExplanation(e.target.value)}
                    />
                </div>
            </section>

            <div className={`${stepStyles.stepButton} ${stepStyles.footer}`}>
                <Button
                    variant="primary"
                    shape="pill"
                    className={`${stepStyles.continueBtn} ${styles.continueBtn}`}
                    label={t("continue")}
                    onClick={handleSaveQuiz}
                    disabled={!hasCorrectAnswer}
                />
            </div>
        </div>
    );
};

export default QuizQuestion;
